// TIP-494 (MV Stage 5) — the dashboard Membership page body, per the approved
// design. Keyed off the active company (TIP-487 switcher) + its profile_status
// (TIP-486 ladder). Three states:
//   claimed/listed → plan selector ("Become a member") + member benefits
//   member         → dual panel: membership benefits (have) + verification (add)
//   verified       → "you're a verified member" confirmation (tab also hidden)
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectActiveCompany } from "../../../../store/activeCompanySlice";
import {
  useRequestVerificationMutation,
  useGetCompanyRequestStatusMutation,
} from "../../../../store/Company/companyApiSlice";
import { CommonToast } from "../../../common/Toast";
import {
  MEMBERSHIP_BENEFITS,
  HAVE_BENEFITS,
  UNLOCK_BENEFITS,
  MEMBERSHIP_PLANS,
} from "./membershipContent";

const NAVY = "#002940";
const ORANGE = "#FB512D";

const StarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill={ORANGE} style={{ flexShrink: 0, marginTop: 1 }}>
    <path d="M12 2 L13.5 9 L21 11 L13.5 13 L12 22 L10.5 13 L3 11 L10.5 9 Z" />
  </svg>
);

const CheckCircle = ({ bg, stroke }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
    <circle cx="12" cy="12" r="10" fill={bg} />
    <path d="M7.5 12.3 10.4 15.2 16.5 8.8" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Benefit = ({ icon, title, body }) => (
  <div style={{ display: "flex", gap: 13, alignItems: "flex-start" }}>
    {icon}
    <div>
      <div style={{ fontSize: 14.5, fontWeight: 600, color: NAVY, marginBottom: 2 }}>{title}</div>
      <div style={{ fontSize: 13, color: "#5A6B73", lineHeight: 1.5 }}>{body}</div>
    </div>
  </div>
);

const sectionCard = (topColor) => ({
  background: "#fff",
  border: "1px solid #E7ECEF",
  borderTop: `3px solid ${topColor}`,
  borderRadius: 14,
  padding: 24,
  boxShadow: "0 1px 2px rgba(0,0,0,.04)",
});

const MembershipStatusCard = () => {
  const active = useSelector(selectActiveCompany);
  const { token, userInfo } = useSelector((state) => state.auth);
  const individualID = userInfo?.individual?.[0]?.individualID;
  const navigate = useNavigate();
  const [requestVerification, { isLoading: isRequesting }] = useRequestVerificationMutation();
  const [getRequestStatus] = useGetCompanyRequestStatusMutation();
  const [plan, setPlan] = useState("yearly");
  const [verifyStatus, setVerifyStatus] = useState("idle"); // idle | review

  // Reflect an already-pending verification request on load (the "in review"
  // state must survive a reload — it isn't just local UI). Members only.
  useEffect(() => {
    const companyID = active?.companyID;
    if (!companyID || active?.profile_status !== "member" || !individualID || !token) return;
    let cancelled = false;
    getRequestStatus({ companyID, individualID, token })
      .unwrap()
      .then((res) => {
        const list = Array.isArray(res) ? res : res?.data || res?.results || [];
        const pending = list.some(
          (r) => String(r?.status || "").toLowerCase() === "pending"
        );
        if (!cancelled && pending) setVerifyStatus("review");
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [active?.companyID, active?.profile_status, individualID, token, getRequestStatus]);

  if (!active?.companyID) {
    return (
      <p style={{ fontSize: 14, color: "#5A6B73", marginTop: 24 }}>
        Select a business from the switcher to manage its membership.
      </p>
    );
  }

  const { companyID, name, profile_status: status } = active;
  const isClaimed = status !== "member" && status !== "verified";
  const isMember = status === "member";
  const isVerified = status === "verified";

  const goToCheckout = (chosen) => {
    window.scrollTo(0, 0);
    navigate(`/membership/${companyID}`, { state: { plan: chosen || plan } });
  };

  const handleRequest = async () => {
    try {
      await requestVerification({ companyID, token }).unwrap();
      setVerifyStatus("review");
      CommonToast.success("Verification requested — our team will review it shortly.");
    } catch (err) {
      CommonToast.error(err?.data?.detail || "Couldn't request verification. Please try again.");
    }
  };

  // Badge for the status row.
  const statusBadge = isVerified ? (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#E1F1E8", color: "#1B7A45", fontSize: 12.5, fontWeight: 600, padding: "5px 12px 5px 10px", borderRadius: 30 }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1B7A45" strokeWidth="2.6"><path d="M5 12.5 10 17.5 19 7" /></svg>
      Verified member
    </span>
  ) : isMember ? (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#F6ECD2", color: "#8A6314", fontSize: 12.5, fontWeight: 600, padding: "5px 12px", borderRadius: 30 }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#C8912F" }} />Member
    </span>
  ) : (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#E3F2FF", color: "#0173C6", fontSize: 12.5, fontWeight: 600, padding: "5px 12px", borderRadius: 30 }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#0194ff" }} />Claimed
    </span>
  );

  return (
    <div>
      {/* status row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 14, padding: "22px 0 18px", marginTop: 14, borderTop: "1px solid #DDE4E9" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: NAVY }}>{name}</h2>
          {statusBadge}
        </div>
        {isClaimed && (
          <span style={{ fontSize: 13.5, color: "#7C8B92", display: "flex", alignItems: "center", gap: 7 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C8912F" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M9 12.5 11 14.5 15 9.5" /></svg>
            Become a member to get verified
          </span>
        )}
      </div>

      {isClaimed && renderClaimed()}
      {isMember && renderMember()}
      {isVerified && renderVerified()}
    </div>
  );

  function renderClaimed() {
    const radio = (on) => ({ width: 22, height: 22, borderRadius: "50%", border: `2px solid ${on ? ORANGE : "#CBD5DB"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 });
    const dot = (on) => ({ width: 11, height: 11, borderRadius: "50%", background: on ? ORANGE : "transparent" });
    const card = (on) => ({ background: "#fff", borderRadius: 14, padding: 22, cursor: "pointer", position: "relative", border: `2px solid ${on ? ORANGE : "#E2E8ED"}`, boxShadow: on ? "0 8px 24px rgba(251,81,45,.14)" : "0 1px 2px rgba(0,0,0,.04)" });
    const ctaLabel = plan === "yearly" ? "Become a member · AED 999 / year" : "Become a member · AED 99 / month";

    return (
      <div>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 8, margin: "20px 0 16px" }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: NAVY }}>Become a member</h3>
          <span style={{ fontSize: 13, color: "#7C8B92" }}>Cancel anytime · billed in AED</span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 18 }}>
          <div onClick={() => setPlan("yearly")} style={card(plan === "yearly")}>
            <span style={{ position: "absolute", top: -11, left: 22, background: ORANGE, color: "#fff", fontSize: 11, fontWeight: 700, letterSpacing: ".5px", textTransform: "uppercase", padding: "4px 11px", borderRadius: 30 }}>Best value</span>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>Yearly</span>
              <span style={radio(plan === "yearly")}><span style={dot(plan === "yearly")} /></span>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, margin: "16px 0 4px" }}>
              <span style={{ fontSize: 34, fontWeight: 700, color: NAVY, letterSpacing: "-.5px", whiteSpace: "nowrap" }}>AED {MEMBERSHIP_PLANS.yearly.price}</span>
              <span style={{ fontSize: 14, color: "#7C8B92" }}>/ year</span>
            </div>
            <div style={{ fontSize: 13, color: "#5A6B73" }}>Just AED {MEMBERSHIP_PLANS.yearly.perMonth} / month, billed annually</div>
            <span style={{ display: "inline-block", marginTop: 12, background: "#FDEBE6", color: "#C53A1C", fontSize: 12, fontWeight: 700, padding: "4px 10px", borderRadius: 6 }}>Save ~16% vs monthly</span>
          </div>

          <div onClick={() => setPlan("monthly")} style={card(plan === "monthly")}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>Monthly</span>
              <span style={radio(plan === "monthly")}><span style={dot(plan === "monthly")} /></span>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, margin: "16px 0 4px" }}>
              <span style={{ fontSize: 34, fontWeight: 700, color: NAVY, letterSpacing: "-.5px", whiteSpace: "nowrap" }}>AED {MEMBERSHIP_PLANS.monthly.price}</span>
              <span style={{ fontSize: 14, color: "#7C8B92" }}>/ month</span>
            </div>
            <div style={{ fontSize: 13, color: "#5A6B73" }}>Billed monthly · most flexible</div>
            <span style={{ display: "inline-block", marginTop: 12, color: "#9AA8AF", fontSize: 12, fontWeight: 600, padding: "4px 0" }}>No long-term commitment</span>
          </div>
        </div>

        <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
          <button type="button" onClick={() => goToCheckout(plan)} style={{ background: ORANGE, color: "#fff", border: "none", borderRadius: 10, padding: "14px 26px", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>{ctaLabel}</button>
          <span style={{ fontSize: 12.5, color: "#9AA8AF", display: "flex", alignItems: "center", gap: 7 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#9AA8AF" strokeWidth="1.9"><rect x="4" y="10" width="16" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>
            Secure checkout · invoice issued instantly
          </span>
        </div>

        <div style={{ marginTop: 42, paddingTop: 30, borderTop: "1px solid #DDE4E9" }}>
          <h3 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 700, color: NAVY }}>What you get with membership</h3>
          <p style={{ margin: "0 0 24px", fontSize: 14, color: "#7C8B92" }}>Five things you get the moment your subscription activates — on either plan.</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "26px 48px" }}>
            {MEMBERSHIP_BENEFITS.map((b) => (
              <Benefit key={b.title} icon={<StarIcon />} title={b.title} body={b.body} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // member (idle / review) and verified all share the dual-panel layout — the
  // verified company, reached by switching companies while on this page, sees
  // the same page a requesting member sees, with the timeline completed.
  function renderMember() {
    return renderDualPanel(verifyStatus); // "idle" | "review"
  }

  function renderVerified() {
    return renderDualPanel("verified");
  }

  function renderDualPanel(vState) {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 22, marginTop: 8 }}>
        {/* LEFT: membership (have) */}
        <section style={sectionCard("#C8912F")}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 5 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#F6ECD2", color: "#8A6314", fontSize: 11, fontWeight: 700, letterSpacing: ".6px", textTransform: "uppercase", padding: "4px 9px", borderRadius: 6 }}>Active</span>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: NAVY }}>Your membership</h3>
          </div>
          <p style={{ margin: "0 0 18px", fontSize: 13.5, color: "#7C8B92", lineHeight: 1.5 }}>You're a paying member — these are working for you right now.</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {HAVE_BENEFITS.map((b) => (
              <Benefit key={b.title} icon={<CheckCircle bg="#F6ECD2" stroke="#9A6B12" />} title={b.title} body={b.body} />
            ))}
          </div>
        </section>

        {/* RIGHT: verification (add / in review / done) */}
        <section style={{ ...sectionCard("#1B7A45"), display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 5 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#E1F1E8", color: "#1B7A45", fontSize: 11, fontWeight: 700, letterSpacing: ".6px", textTransform: "uppercase", padding: "4px 9px", borderRadius: 6 }}>Free perk</span>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: NAVY }}>Add verification</h3>
          </div>
          <p style={{ margin: "0 0 18px", fontSize: 13.5, color: "#7C8B92", lineHeight: 1.5 }}>
            {vState === "verified"
              ? "Your verification is complete — the green Verified badge is live on your public profile."
              : "A free, optional benefit — never a purchase. Request the green Verified badge whenever you're ready."}
          </p>

          {vState === "idle" && (
            <div>
              <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 22 }}>
                {UNLOCK_BENEFITS.map((b) => (
                  <Benefit key={b.title} icon={<CheckCircle bg="#E1F1E8" stroke="#1B7A45" />} title={b.title} body={b.body} />
                ))}
              </div>
              <button type="button" onClick={handleRequest} disabled={isRequesting} style={{ width: "100%", background: ORANGE, color: "#fff", border: "none", borderRadius: 10, padding: 14, fontSize: 15, fontWeight: 700, cursor: "pointer", opacity: isRequesting ? 0.6 : 1 }}>
                {isRequesting ? "Requesting…" : "Request verification"}
              </button>
              <p style={{ margin: "11px 0 0", fontSize: 12, color: "#9AA8AF", textAlign: "center" }}>Sends a request to the TREVEX team — no payment, no commitment.</p>
            </div>
          )}

          {vState === "review" && (
            <div>
              <div style={{ background: "#FFF6F1", border: "1px solid #FBD9CC", borderRadius: 11, padding: 16, display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 20 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.9" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                <div>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: "#C53A1C", marginBottom: 2 }}>Your request has been sent</div>
                  <div style={{ fontSize: 13, color: "#7A5040", lineHeight: 1.5 }}>The TREVEX team will review the documents for your profile data and get back to you.</div>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", marginBottom: 20 }}>
                <TimelineStep done label="Request submitted" sub="Just now" connector />
                <TimelineStep current label="Under review by TREVEX" sub="In progress — the team may ask for documents" connector />
                <TimelineStep label="Verified badge added" sub="Pending" />
              </div>
              <button type="button" onClick={() => setVerifyStatus("idle")} style={{ width: "100%", background: "transparent", color: "#7C8B92", border: "1px solid #DCE3E8", borderRadius: 10, padding: 11, fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>Cancel request</button>
            </div>
          )}

          {vState === "verified" && (
            <div>
              <div style={{ background: "#E8F5EE", border: "1px solid #BFE3CD", borderRadius: 11, padding: 16, display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 20 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1B7A45" strokeWidth="2.4" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="9" /><path d="M8 12.3 11 15.2 16.2 8.8" /></svg>
                <div>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: "#1B7A45", marginBottom: 2 }}>Your business is verified</div>
                  <div style={{ fontSize: 13, color: "#3F6B52", lineHeight: 1.5 }}>Every data point on your profile has been checked by TREVEX. Your Verified badge is live.</div>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", marginBottom: 20 }}>
                <TimelineStep done label="Request submitted" sub="Done" connector />
                <TimelineStep done label="Reviewed by TREVEX" sub="Approved" connector />
                <TimelineStep done label="Verified badge added" sub="Live on your public profile" />
              </div>
              <button type="button" onClick={() => navigate(`/search/${companyID}`)} style={{ width: "100%", background: "transparent", color: ORANGE, border: `1.5px solid ${ORANGE}`, borderRadius: 10, padding: 12, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>View public profile</button>
            </div>
          )}
        </section>
      </div>
    );
  }
};

const TimelineStep = ({ done, current, label, sub, connector }) => (
  <div style={{ display: "flex", gap: 13, alignItems: "flex-start" }}>
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      {done ? (
        <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#1B7A45", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M5 12.5 10 17.5 19 7" /></svg>
        </div>
      ) : current ? (
        <div style={{ width: 22, height: 22, borderRadius: "50%", border: `2px solid ${ORANGE}`, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: ORANGE }} />
        </div>
      ) : (
        <div style={{ width: 22, height: 22, borderRadius: "50%", border: "2px solid #D6DEE3", background: "#fff" }} />
      )}
      {connector && <div style={{ width: 2, flex: 1, background: "#D6DEE3", minHeight: 22 }} />}
    </div>
    <div style={{ paddingBottom: connector ? 14 : 0 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: current || done ? NAVY : "#9AA8AF" }}>{label}</div>
      <div style={{ fontSize: 12.5, color: done || current ? "#9AA8AF" : "#B4BFC5" }}>{sub}</div>
    </div>
  </div>
);

export default MembershipStatusCard;
